import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ActionList, ActionMenu, AvatarStack, Button} from '@primer/react'
import styles from './AssigneeAvatars.module.css'

interface AssigneeAvatar {
  login: string
  avatarUrl: string
}

interface AssigneeAvatarsProps {
  assignees: AssigneeAvatar[]
  /** Invoked with the assignee login when an avatar is clicked. When omitted the avatars are static. */
  onSelect?: (login: string) => void
  /** Class applied to the filter button so each row can preserve its own link styling. */
  filterLinkClassName?: string
  /** Right-align the stack (used in the default-density metadata column). */
  alignRight?: boolean
  /** Limit visible avatars and expose the complete list from an overflow menu. */
  maxVisible?: number
}

/**
 * Shared assignee avatar stack for dashboard list rows. Renders user hovercards
 * so issue and pull-request rows behave identically in the mixed list. Returns
 * `null` when there are no assignees.
 */
export function AssigneeAvatars({
  assignees,
  onSelect,
  filterLinkClassName,
  alignRight = false,
  maxVisible,
}: AssigneeAvatarsProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const visibleAssignees = maxVisible === undefined ? assignees : assignees.slice(0, maxVisible)
  const hiddenCount = assignees.length - visibleAssignees.length

  if (assignees.length === 0) return null

  const renderAssignee = (assignee: AssigneeAvatar) => {
    const hovercardAttributes = getHovercardAttributesForType('user', {login: assignee.login})

    return onSelect ? (
      <button
        key={assignee.login}
        type="button"
        className={filterLinkClassName}
        onClick={() => onSelect(assignee.login)}
        aria-label={`Filter by assignee ${assignee.login}`}
        {...hovercardAttributes}
      >
        <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
      </button>
    ) : (
      <GitHubAvatar
        key={assignee.login}
        src={assignee.avatarUrl}
        size={20}
        alt={assignee.login}
        {...hovercardAttributes}
      />
    )
  }

  if (maxVisible === undefined) {
    return <AvatarStack alignRight={alignRight}>{assignees.map(renderAssignee)}</AvatarStack>
  }

  return (
    <div className={styles.container}>
      <AvatarStack alignRight={alignRight} disableExpand={hiddenCount > 0}>
        {visibleAssignees.map(renderAssignee)}
      </AvatarStack>
      {hiddenCount > 0 && (
        <ActionMenu>
          <ActionMenu.Anchor>
            <Button
              type="button"
              size="small"
              variant="invisible"
              count={`+${hiddenCount}`}
              className={styles.overflowButton}
              aria-label={`+${hiddenCount}, show all ${assignees.length} assignees`}
              onClick={event => event.stopPropagation()}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end" onClick={event => event.stopPropagation()}>
            <ActionList>
              <ActionList.Group>
                <ActionList.GroupHeading>{onSelect ? 'Filter by assignee' : 'Assignees'}</ActionList.GroupHeading>
                {assignees.map(assignee => (
                  <ActionList.Item
                    key={assignee.login}
                    onSelect={onSelect ? () => onSelect(assignee.login) : undefined}
                    inactiveText={onSelect ? undefined : 'Assignee'}
                  >
                    <ActionList.LeadingVisual>
                      <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
                    </ActionList.LeadingVisual>
                    {assignee.login}
                  </ActionList.Item>
                ))}
              </ActionList.Group>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
    </div>
  )
}
